/**
 * Wizard step 2 — financing. Purchase price plus a single acquisition loan
 * (amount → LTV, rate, term, points, first payment date).
 *
 * For BRRRR the user first picks which loan this describes — the hard-money
 * acquisition loan or the refinance (long-term) loan — which re-seeds the term
 * defaults and, for the refinance, exposes an ARV field (its LTV is against ARV,
 * not purchase). The chosen slot is what gets written to `brrrr_hard_money` /
 * `brrrr_long_term`.
 */
import type { UseFormReturn } from "react-hook-form";

import { DateInput } from "@/components/ui/date-input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TermField } from "@/components/financing/TermField";
import type { AddPropertyFormValues } from "@/components/properties/add-property/schema";
import type { BrrrrLoanKind } from "@/lib/add-property";

interface StepFinancingProps {
  form: UseFormReturn<AddPropertyFormValues>;
  onBrrrrKindChange: (kind: BrrrrLoanKind) => void;
}

export function StepFinancing({ form, onBrrrrKindChange }: StepFinancingProps) {
  const strategy = form.watch("strategy");
  const brrrrKind = form.watch("brrrrLoanKind");
  const isBrrrr = strategy === "BRRRR";
  const usesArvBase = isBrrrr && brrrrKind === "long_term";

  const purchase = form.watch("purchase");
  const arv = form.watch("arv");
  const loanValue = form.watch("loanValue");
  const base = usesArvBase ? arv : purchase;
  const ltvPct = base > 0 ? Math.min(999, Math.max(0, (loanValue / base) * 100)) : 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        control={form.control}
        name="purchase"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Purchase price</FormLabel>
            <FormControl>
              <MoneyInput placeholder="245,000" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isBrrrr && (
        <FormField
          control={form.control}
          name="brrrrLoanKind"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Loan type</FormLabel>
              <Select
                value={field.value}
                onValueChange={(v) => {
                  field.onChange(v);
                  onBrrrrKindChange(v as BrrrrLoanKind);
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="hard_money">Hard money</SelectItem>
                  <SelectItem value="long_term">Refinance (long term)</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Every strategy needs an ARV estimate: Flip prices profit off it, BRRRR
          sizes the refinance against it, Rental reads equity from it. */}
      <FormField
        control={form.control}
        name="arv"
        render={({ field }) => (
          <FormItem className={isBrrrr ? undefined : "sm:col-span-2"}>
            <FormLabel>Estimated after-repair value (ARV)</FormLabel>
            <FormControl>
              <MoneyInput placeholder="285,000" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormDescription>
              {usesArvBase
                ? "The refinance LTV is measured against this."
                : "Best estimate of the value once rehab is complete."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="loanValue"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Loan amount</FormLabel>
            <FormControl>
              <MoneyInput placeholder="160,000" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormDescription>
              {base > 0
                ? `${ltvPct.toFixed(0)}% LTV ${usesArvBase ? "of ARV" : "of purchase"}`
                : usesArvBase
                  ? "Enter an ARV to see the LTV."
                  : "Enter a purchase price to see the LTV."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="rate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Interest rate (%)</FormLabel>
            <FormControl>
              <NumberInput step="0.01" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="points"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Loan points (%)</FormLabel>
            <FormControl>
              <NumberInput step="0.1" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="termMonths"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Loan term</FormLabel>
            <FormControl>
              <TermField
                months={field.value}
                unit={form.watch("termUnit")}
                onChange={({ months, unit }) => {
                  field.onChange(months);
                  form.setValue("termUnit", unit, { shouldDirty: true });
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="emiDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>First payment date</FormLabel>
            <FormControl>
              <DateInput {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

/** Number input with a leading `$` affix — repeated across the money fields. */
function MoneyInput({
  value,
  onChange,
  placeholder,
}: {
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
        $
      </span>
      <NumberInput
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="pl-7"
      />
    </div>
  );
}
